/**
 * Linha do tempo ÚNICA do atendimento (spec 018, US3 / FR-005): comentários,
 * notas internas, mensagens do WhatsApp das conversas vinculadas, eventos do
 * sistema (agrupados), apontamentos e OS — o que antes eram as abas
 * Comunicação, Conversas, Histórico e Apontamentos, numa ordem só.
 *
 * O cliente à esquerda, a equipe à direita, nota interna em âmbar, sistema no
 * meio e pequeno. Conversa de mesa que a pessoa não lê aparece como
 * "conversa restrita", sem conteúdo (a API nem o manda).
 */
import { useMemo, useState } from 'react';
import { useInfiniteQuery, infiniteQueryOptions, type InfiniteData } from '@tanstack/react-query';
import {
  Bot,
  CheckCheck,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  EyeOff,
  Lock,
  Paperclip,
  RotateCcw,
  TriangleAlert,
  Wrench,
} from 'lucide-react';
import {
  SERVICE_ORDER_STATUS_LABELS,
  SERVICE_ORDER_TYPE_LABELS,
  deliveryView,
  type ChatMessage,
  type TicketTimeline,
  type TimelineEntry,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/misc';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { useTerms } from '@/shared/tenant-profile';
import { LinkifiedText, MessageMedia, isMediaContent } from '@/features/chats/chat-media';
import { BotMessageBody, WhatsappText } from '@/features/chats/whatsapp-text';
import { OriginBadge, deliveryErrorText } from '@/features/chats/chats-format';
import { MEDIA_AWAIT_POLL_MS, mediaAwaitState } from '@/features/chats/media-await';
import {
  messageKind,
  noteSummary,
  rememberSystemDetails,
  rememberedSystemDetails,
} from '@/features/chats/message-kind';
import { downloadTicketFile } from '@/features/tickets/tickets-mutations';
import { MentionText } from '@/features/tickets/mentions';
import { durationMinutes, eventActorName, eventDetail, eventLabel } from '@/features/tickets/ticket-format';
import { formatFileSize } from '@/features/knowledge/knowledge-format';
import { formatElapsed } from '@/features/work/work-format';
import { TIMELINE_KEY } from './timeline-key';

export { TIMELINE_KEY };

export function ticketTimelineOptions(ticketNumber: number) {
  return infiniteQueryOptions({
    queryKey: [...TIMELINE_KEY, { ticketNumber }] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const res = await api<TicketTimeline>(`/tickets/${ticketNumber}/timeline`, {
        query: { limit: 100, ...(pageParam ? { cursor: pageParam } : {}) },
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** alguma mensagem do WhatsApp na linha do tempo ainda espera a cópia da mídia? */
function awaitsMedia(data: InfiniteData<TicketTimeline> | undefined, now: number): boolean {
  return (data?.pages ?? []).some((page) =>
    page.entries.some(
      (e) =>
        e.kind === 'chat_message' &&
        mediaAwaitState({ contentType: e.contentType, mediaUrl: e.mediaUrl, createdAt: e.at }, now) ===
          'awaiting'
    )
  );
}

/** Páginas chegam do mais novo para trás; a tela lê do mais antigo para a frente. */
export function useTicketTimeline(ticketNumber: number) {
  const q = useInfiniteQuery({
    ...ticketTimelineOptions(ticketNumber),
    // a linha do tempo não tem polling: sem esta reconsulta curta, só o evento
    // realtime tiraria uma mídia de "baixando mídia…" — e evento se perde.
    // Dura enquanto a mídia espera, até o teto (features/chats/media-await).
    refetchInterval: (query) => (awaitsMedia(query.state.data, Date.now()) ? MEDIA_AWAIT_POLL_MS : false),
  });
  const entries = useMemo(
    () => [...(q.data?.pages ?? [])].reverse().flatMap((p) => p.entries),
    [q.data]
  );
  return { ...q, entries };
}

const when = (at: Date | string) =>
  new Date(at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

type Block =
  | { kind: 'entry'; entry: Exclude<TimelineEntry, { kind: 'event' }> }
  | { kind: 'events'; events: Array<Extract<TimelineEntry, { kind: 'event' }>> };

/** eventos em sequência viram UM bloco ("agrupados") — não picotam a conversa */
function blocksOf(entries: TimelineEntry[]): Block[] {
  const blocks: Block[] = [];
  for (const e of entries) {
    const last = blocks[blocks.length - 1];
    if (e.kind === 'event') {
      if (last?.kind === 'events') last.events.push(e);
      else blocks.push({ kind: 'events', events: [e] });
    } else {
      blocks.push({ kind: 'entry', entry: e });
    }
  }
  return blocks;
}

export function Timeline({
  ticketNumber,
  entries,
  isPending,
  isError,
  hasOlder,
  loadingOlder,
  onLoadOlder,
  onResendChatMessage,
  stickySwitch = false,
}: {
  ticketNumber: number;
  entries: TimelineEntry[];
  isPending: boolean;
  isError: boolean;
  hasOlder: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  /** spec 028: "tentar de novo" da resposta que falhou — só quem segura a conversa */
  onResendChatMessage?: ((chatId: number, messageId: number) => void) | null;
  /**
   * spec 060: o switch "Detalhes do sistema" fica preso no topo de quem rola a
   * linha do tempo. A tela Conversas abre rolada para o fim, e sem isto o switch
   * ficava lá em cima, fora da janela. Só onde a linha do tempo é a primeira
   * coisa do contêiner que rola (px-4 py-3; o `-top-3` desconta o padding, que o
   * sticky respeita): o ticket já tem o cabeçalho preso ali.
   */
  stickySwitch?: boolean;
}) {
  // spec 060: as notas do sistema começam recolhidas; o switch mostra todas, e a
  // escolha é a mesma da conversa do /chats (o mesmo navegador)
  const [systemDetails, setSystemDetails] = useState(() =>
    rememberedSystemDetails(() => window.localStorage)
  );
  const toggleSystemDetails = (on: boolean) => {
    rememberSystemDetails(() => window.localStorage, on);
    setSystemDetails(on);
  };
  const showSwitch = useMemo(
    () => entries.some((e) => e.kind === 'chat_message' && messageKind(asChatMessage(e)) === 'system-note'),
    [entries]
  );
  if (isPending) {
    return (
      <div className="space-y-3" aria-busy>
        <Skeleton className="ml-auto h-16 w-2/3" />
        <Skeleton className="h-16 w-2/3" />
        <Skeleton className="mx-auto h-6 w-1/2" />
      </div>
    );
  }
  if (isError) {
    return <p className="py-6 text-center text-sm text-destructive">Não foi possível carregar a linha do tempo.</p>;
  }
  return (
    <div className="flex flex-col gap-2.5" aria-label="Linha do tempo">
      {showSwitch ? (
        <div
          data-system-details-switch
          className={cn(
            'flex justify-end',
            stickySwitch && 'sticky -top-3 z-10 -mx-4 -mt-3 bg-background/95 px-4 py-1.5 backdrop-blur'
          )}
        >
          <span className="flex items-center gap-1.5">
            <Switch
              id={`timeline-system-details-${ticketNumber}`}
              size="sm"
              checked={systemDetails}
              onCheckedChange={toggleSystemDetails}
            />
            <Label htmlFor={`timeline-system-details-${ticketNumber}`} className="text-xs text-muted-foreground">
              Detalhes do sistema
            </Label>
          </span>
        </div>
      ) : null}
      {hasOlder ? (
        <div className="text-center">
          <Button variant="ghost" size="sm" onClick={onLoadOlder} disabled={loadingOlder}>
            {loadingOlder ? <Spinner className="size-4" /> : null}
            Carregar anteriores
          </Button>
        </div>
      ) : null}
      {entries.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">Nada por aqui ainda.</p>
      ) : (
        blocksOf(entries).map((b) =>
          b.kind === 'events' ? (
            <EventGroup key={`ev-${b.events[0]!.id}`} events={b.events} />
          ) : (
            <EntryView
              key={`${b.entry.kind}-${b.entry.id}`}
              entry={b.entry}
              ticketNumber={ticketNumber}
              systemDetails={systemDetails}
              onResendChatMessage={onResendChatMessage ?? null}
            />
          )
        )
      )}
    </div>
  );
}

function EntryView({
  entry,
  ticketNumber,
  systemDetails,
  onResendChatMessage,
}: {
  entry: Exclude<TimelineEntry, { kind: 'event' }>;
  ticketNumber: number;
  /** spec 060: o switch "Detalhes do sistema" — as notas do sistema abertas */
  systemDetails: boolean;
  onResendChatMessage: ((chatId: number, messageId: number) => void) | null;
}) {
  switch (entry.kind) {
    case 'comment':
      return <CommentBubble entry={entry} ticketNumber={ticketNumber} />;
    case 'chat_message':
      return <ChatBubble entry={entry} systemDetails={systemDetails} onResend={onResendChatMessage} />;
    case 'chat_restricted':
      return (
        <div className="mx-auto flex max-w-md items-center gap-2 rounded-md border border-dashed bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <EyeOff className="size-3.5 shrink-0" />
          Conversa restrita — {entry.messages} {entry.messages === 1 ? 'mensagem' : 'mensagens'} numa mesa que você não
          acompanha
          <span className="ml-auto shrink-0 tabular-nums">{when(entry.at)}</span>
        </div>
      );
    case 'appointment':
      return (
        <SystemLine icon={<Clock className="size-3.5" />} at={entry.at}>
          <span className="font-medium text-foreground">{entry.userName}</span>{' '}
          {entry.endedAt
            ? `apontou ${formatElapsed(durationMinutes(entry.startedAt, entry.endedAt) * 60_000)}`
            : 'está com o cronômetro correndo'}
          {entry.description ? ` · ${entry.description}` : ''}
        </SystemLine>
      );
    case 'service_order':
      return (
        <SystemLine icon={<Wrench className="size-3.5" />} at={entry.at}>
          OS nº {entry.number} — {SERVICE_ORDER_TYPE_LABELS[entry.type]} · {SERVICE_ORDER_STATUS_LABELS[entry.status]}
        </SystemLine>
      );
  }
}

function SystemLine({ icon, at, children }: { icon: React.ReactNode; at: Date | string; children: React.ReactNode }) {
  return (
    <div className="mx-auto flex max-w-xl items-center gap-1.5 text-xs text-muted-foreground">
      <span className="shrink-0 text-muted-foreground/80">{icon}</span>
      <span className="min-w-0">{children}</span>
      <span className="shrink-0 tabular-nums opacity-70">{when(at)}</span>
    </div>
  );
}

function EventGroup({ events }: { events: Array<Extract<TimelineEntry, { kind: 'event' }>> }) {
  const terms = useTerms();
  const [open, setOpen] = useState(false);
  const last = events[events.length - 1]!;
  const line = (e: Extract<TimelineEntry, { kind: 'event' }>) => {
    const detail = eventDetail({ type: e.type, payload: e.payload }, terms);
    // sem pessoa: o bot (pela chave) aparece; o sistema, não — como sempre foi
    const actor = eventActorName({ userName: e.userName, payload: e.payload });
    return `${eventLabel(e.type)}${detail ? ` — ${detail}` : ''}${actor !== 'Sistema' ? ` · ${actor}` : ''}`;
  };
  if (events.length === 1) {
    return <SystemLine icon={<ChevronRight className="size-3.5" />} at={last.at}>{line(last)}</SystemLine>;
  }
  return (
    <div className="mx-auto w-full max-w-xl text-xs text-muted-foreground">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left hover:bg-muted/60"
      >
        {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        <span className="min-w-0 truncate">
          {events.length} eventos do sistema · último: {line(last)}
        </span>
        <span className="ml-auto shrink-0 tabular-nums opacity-70">{when(last.at)}</span>
      </button>
      {open ? (
        <ul className="mt-1 space-y-0.5 border-l pl-3">
          {events.map((e) => (
            <li key={e.id} className="flex gap-2">
              <span className="min-w-0 flex-1">{line(e)}</span>
              <span className="shrink-0 tabular-nums opacity-70">{when(e.at)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function CommentBubble({
  entry,
  ticketNumber,
}: {
  entry: Extract<TimelineEntry, { kind: 'comment' }>;
  ticketNumber: number;
}) {
  const [error, setError] = useState<string | null>(null);
  // spec 022: a nota do bot tem a própria cara — não é a equipe falando (nem
  // nota interna de alguém) e nunca foi ao cliente nem ao TiFlux
  if (entry.authorKind === 'bot') return <BotNoteBubble entry={entry} />;
  if (entry.internal) {
    return (
      <article className="rounded-lg border border-[#FFE58F] bg-[#FFFBE6] px-3 py-2">
        <header className="mb-0.5 flex items-center gap-1.5 text-[11px] font-semibold text-[#D48806]">
          <Lock className="size-3" />
          Nota interna · {entry.authorName}
          <span className="ml-auto font-normal text-muted-foreground tabular-nums">{when(entry.at)}</span>
        </header>
        <div className="text-sm break-words whitespace-pre-wrap text-foreground">
          <MentionText body={entry.body} mentions={entry.mentions} />
        </div>
      </article>
    );
  }
  const fromClient = entry.authorKind === 'client';
  const fromSystem = entry.authorKind === 'system';
  return (
    <div className={cn('flex', fromClient || fromSystem ? 'justify-start' : 'justify-end')}>
      <article
        className={cn(
          'max-w-[80%] rounded-lg px-3 py-2',
          fromClient ? 'rounded-bl-sm border bg-card' : fromSystem ? 'border border-dashed bg-muted/40' : 'rounded-br-sm border border-primary/30 bg-secondary'
        )}
      >
        <header className="mb-0.5 flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground">
          {entry.authorName}
          <span className="rounded border px-1 py-px text-[10px] font-medium">
            {fromClient ? 'comentário do cliente' : fromSystem ? 'sistema' : 'comentário público'}
          </span>
        </header>
        <div className="text-sm break-words whitespace-pre-wrap text-foreground">
          <LinkifiedText text={entry.body} />
        </div>
        {entry.files.length > 0 ? (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {entry.files.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() =>
                  downloadTicketFile({ ...f, ticketNumber, commentId: entry.id, uploadedBy: null, createdAt: entry.at }).catch(
                    (e: unknown) => setError(e instanceof Error ? e.message : 'Falha no download')
                  )
                }
                className="inline-flex items-center gap-1 rounded border bg-card px-1.5 py-0.5 text-[11px] hover:border-primary/50"
              >
                <Paperclip className="size-3" />
                {f.fileName}
                <span className="text-muted-foreground">{formatFileSize(f.size)}</span>
              </button>
            ))}
          </div>
        ) : null}
        {error ? <p className="mt-1 text-[11px] text-destructive">{error}</p> : null}
        <div className="mt-0.5 text-right text-[11px] text-muted-foreground tabular-nums">{when(entry.at)}</div>
      </article>
    </div>
  );
}

/**
 * Nota do bot (spec 022): interna, autor "Bot", escrita pela API do bot com o
 * que o cliente respondeu no fluxo. No meio, como o sistema, mas com o texto
 * inteiro — é registro, não conversa.
 */
function BotNoteBubble({ entry }: { entry: Extract<TimelineEntry, { kind: 'comment' }> }) {
  return (
    <article
      aria-label="Nota do bot"
      className="mx-auto w-full max-w-xl rounded-lg border border-violet-200 bg-violet-50/70 px-3 py-2"
    >
      <header className="mb-0.5 flex items-center gap-1.5 text-[11px] font-semibold text-violet-700">
        <Bot className="size-3.5" />
        Nota do bot
        {entry.internal ? (
          <span className="inline-flex items-center gap-0.5 font-normal text-violet-600/80">
            <Lock className="size-3" />
            interna
          </span>
        ) : null}
        <span className="ml-auto font-normal text-muted-foreground tabular-nums">{when(entry.at)}</span>
      </header>
      <div className="text-sm break-words whitespace-pre-wrap text-foreground">{entry.body}</div>
    </article>
  );
}

/** a entrada da linha do tempo no formato da mensagem do /chats — para reusar a mídia */
function asChatMessage(e: Extract<TimelineEntry, { kind: 'chat_message' }>): ChatMessage {
  return {
    id: e.id,
    chatId: e.chatId,
    direction: e.direction,
    authorType: e.authorType,
    authorName: e.authorName,
    internal: e.internal,
    text: e.text,
    contentType: e.contentType,
    mediaMime: e.mediaMime,
    mediaFilename: e.mediaFilename,
    mediaSize: e.mediaSize,
    caption: e.caption,
    mediaUrl: e.mediaUrl,
    wamid: null,
    deliveryStatus: e.deliveryStatus,
    deliveryError: e.deliveryError ?? null,
    resentAt: e.resentAt ?? null,
    createdAt: e.at,
  };
}

function ChatBubble({
  entry,
  systemDetails,
  onResend,
}: {
  entry: Extract<TimelineEntry, { kind: 'chat_message' }>;
  systemDetails: boolean;
  /** spec 028: "tentar de novo" — só quem segura a conversa recebe */
  onResend?: ((chatId: number, messageId: number) => void) | null;
}) {
  // spec 060: o que a mensagem é (message-kind.ts) — a nota de um atendente, o
  // registro do sistema e o que o sistema mandou ao cliente não saem mais no
  // mesmo amarelo
  const message = asChatMessage(entry);
  const kind = messageKind(message);
  if (kind === 'bot') {
    // spec 028 (FR-005): a fala do bot externo — saída, mas não da equipe
    return (
      <div className="flex justify-end">
        <article className="max-w-[80%] rounded-lg rounded-br-sm border border-violet-200 bg-violet-50 px-3 py-2">
          <header className="mb-0.5 flex items-center gap-1.5 text-[11px] font-semibold text-violet-700">
            <Bot className="size-3.5" />
            bot
            <span className="ml-auto font-normal text-muted-foreground tabular-nums">{when(entry.at)}</span>
          </header>
          {/* formatado como no WhatsApp, e os botões/lista como opções só de leitura */}
          <BotMessageBody text={entry.text} />
        </article>
      </div>
    );
  }
  if (kind === 'system-note') return <SystemNoteBlock entry={entry} expanded={systemDetails} />;
  if (kind === 'system-event') {
    return (
      <SystemLine icon={<ChevronRight className="size-3.5" />} at={entry.at}>
        {entry.internal ? <span className="mr-1 font-semibold text-[#D48806]">Interno ·</span> : null}
        {entry.text}
      </SystemLine>
    );
  }
  if (kind === 'internal-comment') {
    return (
      <article className="rounded-lg border border-[#FFE58F] bg-[#FFFBE6] px-3 py-2">
        <header className="mb-0.5 flex items-center gap-1.5 text-[11px] font-semibold text-[#D48806]">
          <Lock className="size-3" />
          Nota interna na conversa · {entry.authorName ?? ''}
          <span className="ml-auto font-normal text-muted-foreground tabular-nums">{when(entry.at)}</span>
        </header>
        <div className="text-sm break-words whitespace-pre-wrap">{entry.text}</div>
      </article>
    );
  }
  const inbound = entry.direction === 'inbound';
  // spec 060: o que o sistema mandou ao cliente (o modelo do disparo, o menu do
  // bot do ERP) é conversa, mas não é da equipe: saída, em tom neutro, com selo
  const automatic = kind === 'automatic';
  const neutral = inbound || automatic;
  return (
    <div className={cn('flex', inbound ? 'justify-start' : 'justify-end')}>
      <article
        className={cn(
          'max-w-[80%] rounded-lg px-3 py-2',
          inbound
            ? 'rounded-bl-sm border bg-card text-foreground'
            : automatic
              ? 'rounded-br-sm border bg-muted text-foreground'
              : 'rounded-br-sm bg-primary text-primary-foreground'
        )}
      >
        <header
          className={cn(
            'mb-0.5 flex items-center gap-1.5 text-[11px] font-semibold',
            neutral ? 'text-muted-foreground' : 'text-primary-foreground/80'
          )}
        >
          {automatic ? 'Mensagem automática' : entry.authorName}
          <OriginBadge origin={entry.chatOrigin} short className="px-1 py-0 text-[10px]" />
        </header>
        {isMediaContent(message) ? (
          <MessageMedia message={message} inbound={inbound} />
        ) : (
          <div className="text-sm break-words whitespace-pre-wrap">
            {/* no WhatsApp, a formatação de lá: `*negrito*` sai em negrito */}
            {entry.chatOrigin === 'whatsapp' ? (
              <WhatsappText text={entry.text} />
            ) : (
              <LinkifiedText text={entry.text} />
            )}
          </div>
        )}
        <div
          className={cn(
            'mt-0.5 flex items-center justify-end gap-1 text-[11px] tabular-nums',
            neutral ? 'text-muted-foreground' : 'text-primary-foreground/70'
          )}
        >
          {when(entry.at)}
          {!inbound && entry.deliveryStatus ? (
            <Ticks status={entry.deliveryStatus} sentAt={entry.at} neutral={automatic} />
          ) : null}
        </div>
        {!inbound && entry.deliveryStatus === 'failed' ? (
          <FailedFooter entry={entry} onResend={onResend ?? null} neutral={automatic} />
        ) : null}
      </article>
    </div>
  );
}

/**
 * O registro do sistema com detalhes (spec 060): a transferência do bot externo e
 * a conversa na Blip antes do nosso bot. Recolhido, é uma linha (a primeira da
 * nota) com "ver detalhes"; aberto (por ela ou pelo switch), o bloco inteiro, em
 * cinza — o âmbar é da nota de uma pessoa. Só a equipe vê: a nota é interna.
 */
function SystemNoteBlock({
  entry,
  expanded,
}: {
  entry: Extract<TimelineEntry, { kind: 'chat_message' }>;
  expanded: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!expanded && !open) {
    return (
      <div data-system-note="recolhida">
        <SystemLine icon={<ChevronRight className="size-3.5" />} at={entry.at}>
          <span className="mr-1 font-semibold">Sistema ·</span>
          {noteSummary(entry.text)}
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="ml-1.5 font-medium text-foreground/70 underline-offset-2 outline-none hover:underline focus-visible:underline"
          >
            ver detalhes
          </button>
        </SystemLine>
      </div>
    );
  }
  return (
    <article className="rounded-lg border bg-muted/60 px-3 py-2" data-system-note="aberta">
      <header className="mb-0.5 flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground">
        <Lock className="size-3" />
        Registro do sistema · só a equipe vê
        {expanded ? null : (
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="font-medium underline-offset-2 outline-none hover:underline focus-visible:underline"
          >
            recolher
          </button>
        )}
        <span className="ml-auto font-normal tabular-nums">{when(entry.at)}</span>
      </header>
      <div className="text-xs break-words whitespace-pre-wrap text-foreground">{entry.text}</div>
    </article>
  );
}

/** "Não entregue" com o motivo curto e o "tentar de novo" (spec 028, FR-006) */
function FailedFooter({
  entry,
  onResend,
  neutral = false,
}: {
  entry: Extract<TimelineEntry, { kind: 'chat_message' }>;
  onResend: ((chatId: number, messageId: number) => void) | null;
  /** spec 060: na bolha neutra da mensagem automática, e não na teal */
  neutral?: boolean;
}) {
  const code = entry.deliveryError?.code ?? null;
  const resendable = entry.authorType === 'attendant' && !entry.internal;
  return (
    <div
      className={cn(
        'mt-1 flex flex-wrap items-center justify-end gap-x-2 border-t pt-1 text-[11px]',
        neutral ? 'border-border' : 'border-primary-foreground/25'
      )}
    >
      <span
        className={cn('font-semibold', neutral ? 'text-destructive' : 'text-primary-foreground')}
        title={code ? `código: ${code}` : undefined}
      >
        Não entregue · {deliveryErrorText(entry.deliveryError)}
      </span>
      {!resendable ? null : entry.resentAt ? (
        <span className="text-primary-foreground/80">Reenviada</span>
      ) : onResend ? (
        <button
          type="button"
          onClick={() => onResend(entry.chatId, entry.id)}
          className="inline-flex items-center gap-1 rounded-sm bg-primary-foreground px-1.5 py-0.5 font-semibold text-primary outline-none hover:bg-primary-foreground/90 focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <RotateCcw className="size-3" aria-hidden />
          Tentar de novo
        </button>
      ) : null}
    </div>
  );
}

/** a MESMA regra da conversa (`deliveryView`, 20/10): sem confirmação depois do prazo, diz em texto */
function Ticks({
  status,
  sentAt,
  neutral = false,
}: {
  status: NonNullable<ChatMessage['deliveryStatus']>;
  sentAt: string | Date;
  /** spec 060: na bolha neutra da mensagem automática, os tons que se leem sobre ela */
  neutral?: boolean;
}) {
  if (deliveryView(status, sentAt, Date.now()) === 'unconfirmed') {
    return (
      <span
        title="Sem confirmação de entrega — o WhatsApp não confirmou que a mensagem chegou. Confira o número do contato."
        aria-label="Sem confirmação de entrega"
        className={cn('inline-flex items-center gap-0.5', neutral ? 'text-[#D48806]' : 'text-[#FFE58F]')}
      >
        <TriangleAlert className="size-3.5" />
        sem confirmação
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <TriangleAlert
        aria-label="Falha no envio"
        className={cn('size-3.5', neutral ? 'text-destructive' : 'text-[#FFA39E]')}
      />
    );
  }
  if (status === 'read') {
    return <CheckCheck aria-label="Lida" className={cn('size-3.5', neutral ? 'text-primary' : 'text-[#03363d]')} />;
  }
  if (status === 'delivered') return <CheckCheck aria-label="Entregue" className="size-3.5" />;
  return <Check aria-label="Enviada" className="size-3.5" />;
}
